// This component will now be used as a lightweight prompt that directs to full pages
// rather than containing the actual auth forms

"use client"

import { motion, AnimatePresence } from "framer-motion"
import { Button } from "@/components/ui/button"
import { X, UserPlus, LogIn } from "lucide-react"

type AuthPromptModalProps = {
  isOpen: boolean
  onClose: () => void
  onLogin: () => void
  onSignup: () => void
  reason: "save" | "premium" | "history"
  returnToScreen?: string
}

export function AuthPromptModal({ isOpen, onClose, onLogin, onSignup, reason, returnToScreen }: AuthPromptModalProps) {
  if (!isOpen) return null

  const getTitle = () => {
    switch (reason) {
      case "save":
        return "Save Your Progress"
      case "premium":
        return "Unlock Premium Features"
      case "history":
        return "Access Your History"
      default:
        return "Create an Account"
    }
  }

  const getMessage = () => {
    switch (reason) {
      case "save":
        return "Sign up to save your message history and access it from any device."
      case "premium":
        return "Create an account to unlock premium features and get unlimited message scans."
      case "history":
        return "Sign up to view your message history and track your improvement over time."
      default:
        return "Create an account to unlock all features and save your progress."
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative w-full max-w-md glass-card rounded-2xl p-6 shadow-xl"
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 text-zinc-400 hover:text-white hover:bg-white/10 rounded-full"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-light mb-2">
                <span className="gradient-text font-medium">{getTitle()}</span>
              </h2>
              <p className="text-zinc-400 text-sm">{getMessage()}</p>
            </div>

            <div className="space-y-4">
              <Button
                className="w-full gradient-bg text-white hover:opacity-90 rounded-xl h-12 font-light"
                onClick={onSignup}
              >
                <UserPlus className="mr-2 h-5 w-5" />
                Sign Up
              </Button>

              <Button
                variant="outline"
                className="w-full border-zinc-700 text-zinc-300 rounded-xl hover:bg-white/10 hover:text-white"
                onClick={onLogin}
              >
                <LogIn className="mr-2 h-5 w-5" />
                Log In
              </Button>

              <Button
                variant="ghost"
                className="w-full text-zinc-400 hover:text-white hover:bg-white/10"
                onClick={onClose}
              >
                Continue as Guest
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

